import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import {
  HANDBOOK_SECTIONS, HANDBOOK_VERSION, HANDBOOK_EFFECTIVE_DATE,
  HANDBOOK_COMPANY, HANDBOOK_DISCLAIMER,
} from "@/constants/handbook";
import { AlertTriangle, CheckCircle2 } from "lucide-react";

interface HandbookConfig { required_version: string; required_since: string; }

export function HandbookSignoff() {
  const { employee, refreshEmployee } = useAuth();
  const [config, setConfig] = useState<HandbookConfig | null>(null);
  const [needsSign, setNeedsSign] = useState(false);
  const [showSigner, setShowSigner] = useState(false);

  useEffect(() => {
    void check();
  }, [employee?.id]);

  async function check() {
    if (!employee) return;
    const { data } = await supabase.from("hq_handbook_config").select("required_version,required_since").eq("id", 1).maybeSingle();
    if (!data) return;
    setConfig(data as HandbookConfig);
    const { data: sigs } = await supabase.from("hq_handbook_signatures")
      .select("id").eq("employee_id", employee.id).eq("handbook_version", (data as HandbookConfig).required_version)
      .limit(1);
    const signed = (sigs?.length ?? 0) > 0;
    if (employee.role !== "kiosk" && employee.role !== "investor" && !signed) setNeedsSign(true);
  }

  if (!employee || !needsSign || !config) return null;

  return (
    <>
      <div className="fixed inset-x-0 top-0 z-[70] bg-orange-500 px-4 py-2 text-center text-sm font-semibold text-white shadow no-print">
        <AlertTriangle className="mr-1 inline" size={14} /> Action required: sign the {HANDBOOK_COMPANY} handbook (v{config.required_version}).
        <button onClick={() => setShowSigner(true)} className="ml-3 rounded bg-white px-3 py-0.5 text-orange-700 hover:bg-orange-50">Read & sign</button>
        <Link to="/handbook" className="ml-2 underline">View only</Link>
      </div>
      {showSigner && (
        <SignerModal
          version={config.required_version}
          onClose={() => setShowSigner(false)}
          onSigned={async () => { setShowSigner(false); setNeedsSign(false); await refreshEmployee(); }}
        />
      )}
    </>
  );
}

function SignerModal({ version, onClose, onSigned }: { version: string; onClose: () => void; onSigned: () => Promise<void> }) {
  const { employee } = useAuth();
  const [scrolledToEnd, setScrolledToEnd] = useState(false);
  const [acked, setAcked] = useState(false);
  const [typedName, setTypedName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const expectedName = [employee?.first_name, employee?.last_name].filter(Boolean).join(" ").trim();

  function onScroll(e: React.UIEvent<HTMLDivElement>) {
    const el = e.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 40) setScrolledToEnd(true);
  }

  async function sign() {
    if (!employee) return;
    if (!acked) { setError("Confirm you've read the handbook."); return; }
    const name = typedName.trim();
    if (!name) { setError("Type your full name to sign."); return; }
    if (expectedName && name.toLowerCase() !== expectedName.toLowerCase()) {
      setError(`Name must match your account name: ${expectedName}`); return;
    }
    setBusy(true); setError(null);

    // Idempotent: if a signature for this employee + version already exists, skip insert
    const { data: existing } = await supabase.from("hq_handbook_signatures")
      .select("id").eq("employee_id", employee.id).eq("handbook_version", version).limit(1);
    if (!existing || existing.length === 0) {
      const { error: e } = await supabase.from("hq_handbook_signatures").insert({
        employee_id: employee.id,
        handbook_version: version,
        signed_name: name,
        user_agent: navigator.userAgent,
        language: employee.language_preference || "en",
      });
      if (e) {
        // If it's a race-condition duplicate, treat as success
        const looksDup = (e.message || "").toLowerCase().includes("duplicate")
          || (e.message || "").toLowerCase().includes("unique");
        if (!looksDup) {
          setBusy(false);
          setError(e.message);
          return;
        }
      }
    }
    // Best-effort update — if RLS blocks or network blips, the signature row is what matters.
    try {
      await supabase.from("hq_employees")
        .update({ handbook_acknowledged_at: new Date().toISOString() })
        .eq("id", employee.id);
    } catch { /* non-blocking */ }
    setBusy(false);
    await onSigned();
  }

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-ink/60 p-4" onClick={() => !busy && onClose()}>
      <div className="card w-full max-w-3xl" onClick={e => e.stopPropagation()}>
        <h2 className="mb-1 text-xl font-bold">Read &amp; sign the handbook</h2>
        <p className="mb-3 text-xs text-gray2">{HANDBOOK_COMPANY} · Handbook v{version} · Effective {HANDBOOK_EFFECTIVE_DATE}</p>

        <div onScroll={onScroll} className="mb-3 max-h-[55vh] space-y-5 overflow-y-auto rounded-lg bg-offwhite p-4 text-sm leading-relaxed">
          {HANDBOOK_SECTIONS.map(s => (
            <section key={s.id}>
              <h3 className="mb-1 font-bold">{s.title}</h3>
              <div className="whitespace-pre-wrap text-gray2">{s.body}</div>
            </section>
          ))}
          <p className="border-t border-line pt-3 text-xs italic text-gray2">{HANDBOOK_DISCLAIMER}</p>
          <p className="text-xs text-teal-dark">⬇ You've reached the end. Confirm below.</p>
        </div>

        {!scrolledToEnd && (
          <p className="mb-2 text-xs text-warn">Scroll to the end before signing.</p>
        )}

        {error && <div className="mb-2 rounded bg-bad-bg px-3 py-2 text-sm text-bad">{error}</div>}

        <label className="mb-3 flex cursor-pointer items-start gap-2 rounded-lg ring-1 ring-line p-3 hover:ring-teal">
          <input type="checkbox" checked={acked} disabled={!scrolledToEnd} onChange={e => setAcked(e.target.checked)} className="mt-0.5" />
          <span className="text-sm">I have read and agree to abide by the {HANDBOOK_COMPANY} Employee Handbook, version {version}.</span>
        </label>

        <div className="mb-3">
          <label className="label">Type your full name to sign</label>
          <input className="input" disabled={!scrolledToEnd || !acked} value={typedName} onChange={e => setTypedName(e.target.value)} placeholder={expectedName || "First Last"} />
          <p className="mt-1 text-xs text-gray2">Your signature, IP, browser, and timestamp will be saved. This is a legal record.</p>
        </div>

        <div className="flex justify-end gap-2">
          <button onClick={onClose} disabled={busy} className="btn-secondary">Later</button>
          <button onClick={() => void sign()} disabled={busy || !scrolledToEnd || !acked || !typedName.trim()} className="btn flex items-center gap-2">
            <CheckCircle2 size={14} /> {busy ? "Signing…" : "Sign handbook"}
          </button>
        </div>
      </div>
    </div>
  );
}
